"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";

interface Feature12Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  featureItemsWithImage?: boolean;
  items?: {
    text?: string;
    image?: string;
    imageAltText?: string;
  }[];
  themeChangeOnScroll?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature12 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 2",
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  featureItemsWithImage = false,
  items,
  themeChangeOnScroll = false,
  attributes,
}: Feature12Props) => {

  const absoluteElementBgClass = cn(
    variant === "A" ? "from-background via-transparent to-transparent" : "from-muted via-transparent to-transparent"
  )

  const IconElem = ({ image, text, imageAltText }: { image?: string; text?: string; imageAltText?: string }) => (
    <div className="flex flex-col items-center">
      {image && featureItemsWithImage && (
        <Image
          src={image}
          alt={imageAltText ?? ""}
          title={imageAltText ?? ""}
          className={cn(
            "h-14 w-14 rounded-lg object-cover p-0 md:block", 
            text ? "hidden" : "block"
          )} 
          width={56}
          height={56}
        />
      )}
      {text && (
        <Badge variant="secondary">
          {text}
        </Badge>
      )}
    </div>
  );

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature12"
      attributes={attributes}
      className="!pb-0"
    >
      <div className="container">
        <div className="flex flex-col items-center gap-6" data-aos="fade-up">
          {badge && (
            <Badge variant="outline" bold>
              {badge}
            </Badge>
          )}
          {title && (
            <h2 className="text-center">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="paragraph-lg text-muted-foreground max-w-3xl text-center text-balance">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex flex-col">
                <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                  <Link href={buttonLink ?? ""}>
                    {buttonText}
                  </Link>
                </Button>
                {microCopy && (
                  <p className="paragraph-sm text-muted-foreground text-center mt-2">
                    {microCopy}
                  </p>
                )}
              </div>
              {secondaryButton && (
                <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                  <Link href={secondaryButtonLink ?? ""}>
                    {secondaryButtonText}
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
        
        <div className="relative mx-auto flex max-w-3xl flex-col gap-5 pt-20 md:pt-40 overflow-hidden pb-8 md:gap-0 z-[3]">
          <div className="grid grid-cols-4 sm:hidden gap-6 justify-center mb-10 z-3" data-aos="fade-up" data-aos-delay="100">
            {items && items.map((item, index) => (
              <IconElem
                key={index}
                image={item.image}
                text={item.text}
                imageAltText={item.imageAltText}
              />
            ))}
          </div>

          <div data-aos="fade-up" data-aos-delay="100" className="hidden sm:block z-[3]">
            {items && (
              <>
                {/* First row: 2 items */}
                <div className="flex justify-around md:mb-[-30px] md:min-h-[100px]">
                  {items[0] && (
                    <IconElem
                      image={items[0].image}
                      text={items[0].text}
                      imageAltText={items[0].imageAltText}
                    />
                  )}
                  {items[1] && (
                    <IconElem
                      image={items[1].image}
                      text={items[1].text}
                      imageAltText={items[1].imageAltText}
                    />
                  )}
                </div>
                
                {/* Second row: 1 item */}
                <div className="flex justify-center md:mb-[-30px] md:min-h-[100px]">
                  {items[2] && (
                    <IconElem
                      image={items[2].image}
                      text={items[2].text}
                      imageAltText={items[2].imageAltText}
                    />
                  )}
                </div>
                
                {/* Third row: 2 items */}
                <div className="flex justify-evenly md:mb-[-30px] md:min-h-[100px]">
                  {items[3] && (
                    <IconElem
                      image={items[3].image}
                      text={items[3].text}
                      imageAltText={items[3].imageAltText}
                    />
                  )}
                  {items[4] && (
                    <IconElem
                      image={items[4].image}
                      text={items[4].text}
                      imageAltText={items[4].imageAltText}
                    />
                  )}
                </div>
                
                {/* Fourth row: 2 items */}
                <div className="flex justify-between">
                  {items[5] && (
                    <IconElem
                      image={items[5].image}
                      text={items[5].text}
                      imageAltText={items[5].imageAltText}
                    />
                  )}
                  {items[6] && (
                    <IconElem
                      image={items[6].image}
                      text={items[6].text}
                      imageAltText={items[6].imageAltText}
                    />
                  )}
                  {items[7] && (
                    <IconElem
                      image={items[7].image}
                      text={items[7].text}
                      imageAltText={items[7].imageAltText}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          <div className="absolute inset-0 z-[0]">
            {[...Array(9)].map((_, index) => (
              <div
                key={index}
                className="absolute bottom-0 left-1/2 aspect-square rounded-full"
                style={{
                  width: 80 + 100 * index + "px",
                  transform: "translateX(-50%) translateY(50%)",
                  background: "var(--color-gradient-primary)",
                  padding: "2px",
                  zIndex: 9 - index
                }}
              >
                <div 
                  className="w-full h-full rounded-full bg-background"
                  style={{
                    width: "100%",
                    height: "100%"
                  }}
                />
              </div>
            ))}
          </div>

          <div className={cn("absolute inset-0 z-[2] bg-linear-to-b", absoluteElementBgClass)} />
          <div className={cn("absolute inset-0 z-[2] bg-linear-to-l", absoluteElementBgClass)} />
          <div className={cn("absolute inset-0 z-[2] bg-linear-to-r", absoluteElementBgClass)} />
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Feature12 };



